import { HttpErrorResponse } from '@angular/common/http';
import { ApiException } from '@core/services/api-client.generated';

/**
 * A failed application-authority request, reduced to what the UI may act on: the HTTP status
 * and the problem document's stable `code` (and `currentRevision` for a revision conflict).
 * The API puts nothing else in these bodies (docs/how-to/administer-application-roles.md).
 */
export interface AuthorityProblem {
  status: number;
  code: string | null;
  currentRevision: number | null;
}

/** Codes the role-administration endpoints use for their distinct responses. */
export const AUTHORITY_PROBLEM_CODES = {
  authorityUnavailable: 'authority-unavailable',
  outcomeUnknown: 'role-change-outcome-unknown',
  revisionConflict: 'revision-conflict',
  lastActiveAdministrator: 'last-active-administrator',
  targetDeactivated: 'target-deactivated',
  impersonationNotAllowed: 'impersonation-not-allowed',
  operationIdReused: 'operation-id-reused',
  investigatorNotFound: 'investigator-not-found',
} as const;

/**
 * Reads the status and problem code from whatever the generated client or HttpClient threw.
 * Anything unrecognisable (a network failure, a client-side exception) is status 0: the caller
 * must treat it as "we do not know", never as a success or a denial.
 */
export function toAuthorityProblem(error: unknown): AuthorityProblem {
  let status = 0;
  let body: unknown = null;
  if (ApiException.isApiException(error)) {
    status = error.status;
    body = parseJson(error.response);
  } else if (error instanceof HttpErrorResponse) {
    status = error.status;
    body =
      typeof error.error === 'string' ? parseJson(error.error) : error.error;
  }

  const record =
    body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  return {
    status,
    code: typeof record['code'] === 'string' ? record['code'] : null,
    currentRevision:
      typeof record['currentRevision'] === 'number'
        ? record['currentRevision']
        : null,
  };
}

function parseJson(text: string | null | undefined): unknown {
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
